import { useState, useRef, useEffect } from "react";
import { useReportModal } from "../../contexts/ReportContext";
import { useLayerPanelHidden } from "../../contexts/LayerPanelContext";
import { Box } from "@mui/material";
import { Layer } from "../../types/layer";
import { Filter } from "./FilterModal";
import FuelsGroup from "./FuelsGroup";
import AssetsControl from "./AssetsControl";
import TreatmentsControl from "./TreatmentsControl";
import IgnitionsControl from "./IgnitionsControl";
import NotesControl from "./NotesControl";
import FireHistoryControl from "./FireHistoryControl";
import PropertyBoundaryControl from "./PropertyBoundaryControl";
import RealtimeControl from "./RealtimeControl";
import CustomLayersControl from "./CustomLayersControl";
import MeasureControl from "./MeasureControl";
import { IncidentGroupVisibility } from "../layer_components/IncidentsLayer";

interface LayerControlPanelProps {
  vegetationLayer: Layer | null;
  fuelLoadLayer: Layer | null;
  grassCuringLayer?: Layer | null;
  fuelLoad3DLayer: Layer | null;
  assetsLayer: Layer | null;
  treatmentsLayer: Layer | null;
  treatmentsFilters: Filter[];
  onTreatmentsFiltersChange: (filters: Filter[]) => void;
  ignitionsLayer: Layer | null;
  ignitionsFilters: Filter[];
  onIgnitionsFiltersChange: (filters: Filter[]) => void;
  notesLayer: Layer | null;
  notesFilters: Filter[];
  onNotesFiltersChange: (filters: Filter[]) => void;
  fireHistoryLayer: Layer | null;
  fireHistoryFilters: Filter[];
  onFireHistoryFiltersChange: (filters: Filter[]) => void;
  fireHistoryModalRef?: React.MutableRefObject<{ open: () => void } | null>;
  propertyBoundaryLayer: Layer | null;
  propertyBoundaryFilters: Filter[];
  onPropertyBoundaryFiltersChange: (filters: Filter[]) => void;
  hotspotsLayer: Layer | null;
  hotspotsFilters: Filter[];
  onHotspotsFiltersChange: (filters: Filter[]) => void;
  hotspotsAvailable: boolean;
  lightningLayer: Layer | null;
  lightningFilters: Filter[];
  onLightningFiltersChange: (filters: Filter[]) => void;
  lightningAvailable: boolean;
  hazardsLayer: Layer | null;
  hazardsFilters: Filter[];
  onHazardsFiltersChange: (filters: Filter[]) => void;
  hazardsAvailable: boolean;
  ignitionPointsLayer: Layer | null;
  ignitionPointsFilters: Filter[];
  onIgnitionPointsFiltersChange: (filters: Filter[]) => void;
  ignitionPointsAvailable: boolean;
  burntAreasLayer: Layer | null;
  burntAreasFilters: Filter[];
  onBurntAreasFiltersChange: (filters: Filter[]) => void;
  burntAreasAvailable: boolean;
  incidentsLayer?: (Layer & { incidentCount?: number }) | null;
  incidentDays?: number;
  onIncidentDaysChange?: (days: number) => void;
  incidentGroups?: IncidentGroupVisibility;
  onIncidentGroupsChange?: (groups: IncidentGroupVisibility) => void;
  customLayersLayer: Layer | null;
  customLayersFilters: Filter[];
  onCustomLayersFiltersChange: (filters: Filter[]) => void;
  customLayersSelectedIds: number[];
  onCustomLayersToggle: (id: number) => void;
  measureLayer: Layer | null;
}

const LayerControlPanel: React.FC<LayerControlPanelProps> = ({
  vegetationLayer,
  fuelLoadLayer,
  grassCuringLayer,
  fuelLoad3DLayer,
  assetsLayer,
  treatmentsLayer,
  treatmentsFilters,
  onTreatmentsFiltersChange,
  ignitionsLayer,
  ignitionsFilters,
  onIgnitionsFiltersChange,
  notesLayer,
  notesFilters,
  onNotesFiltersChange,
  fireHistoryLayer,
  fireHistoryFilters,
  onFireHistoryFiltersChange,
  fireHistoryModalRef,
  propertyBoundaryLayer,
  propertyBoundaryFilters,
  onPropertyBoundaryFiltersChange,
  hotspotsLayer,
  hotspotsFilters,
  onHotspotsFiltersChange,
  hotspotsAvailable,
  lightningLayer,
  lightningFilters,
  onLightningFiltersChange,
  lightningAvailable,
  hazardsLayer,
  hazardsFilters,
  onHazardsFiltersChange,
  hazardsAvailable,
  ignitionPointsLayer,
  ignitionPointsFilters,
  onIgnitionPointsFiltersChange,
  ignitionPointsAvailable,
  burntAreasLayer,
  burntAreasFilters,
  onBurntAreasFiltersChange,
  burntAreasAvailable,
  incidentsLayer = null,
  incidentDays = 30,
  onIncidentDaysChange,
  incidentGroups,
  onIncidentGroupsChange,
  customLayersLayer,
  customLayersFilters,
  onCustomLayersFiltersChange,
  customLayersSelectedIds,
  onCustomLayersToggle,
  measureLayer,
}) => {
  const { open: reportModalOpen } = useReportModal();
  const layerPanelHidden = useLayerPanelHidden();
  const [expanded, setExpanded] = useState(false);
  const collapseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handlePanelMouseEnter = () => {
    if (collapseTimeoutRef.current) {
      clearTimeout(collapseTimeoutRef.current);
      collapseTimeoutRef.current = null;
    }
    setExpanded(true);
  };

  const handlePanelMouseLeave = () => {
    collapseTimeoutRef.current = setTimeout(() => {
      setExpanded(false);
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current);
      }
    };
  }, []);

  if (reportModalOpen || layerPanelHidden) return null;

  return (
    <Box
      onMouseEnter={handlePanelMouseEnter}
      onMouseLeave={handlePanelMouseLeave}
      sx={{
        position: "fixed",
        right: 10,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 10000,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        overflowY: "auto",
        maxHeight: "calc(var(--vh100) * 0.9)",
        paddingRight: "8px",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "rgba(255,255,255,0.3)",
          borderRadius: "3px",
        },
      }}
    >
      <FuelsGroup
        vegetationLayer={vegetationLayer}
        fuelLoadLayer={fuelLoadLayer}
        grassCuringLayer={grassCuringLayer ?? null}
        fuelLoad3DLayer={fuelLoad3DLayer}
        expanded={expanded}
        onExpandedChange={setExpanded}
      />
      <AssetsControl layer={assetsLayer} expanded={expanded} onExpandedChange={setExpanded} />
      <TreatmentsControl layer={treatmentsLayer} filters={treatmentsFilters} onFiltersChange={onTreatmentsFiltersChange} expanded={expanded} onExpandedChange={setExpanded} />
      <IgnitionsControl layer={ignitionsLayer} filters={ignitionsFilters} onFiltersChange={onIgnitionsFiltersChange} expanded={expanded} onExpandedChange={setExpanded} />
      <NotesControl layer={notesLayer} filters={notesFilters} onFiltersChange={onNotesFiltersChange} expanded={expanded} onExpandedChange={setExpanded} />
      
      <Box sx={{ height: "16px" }} />
      
      <FireHistoryControl layer={fireHistoryLayer} filters={fireHistoryFilters} onFiltersChange={onFireHistoryFiltersChange} expanded={expanded} onExpandedChange={setExpanded} addModalRef={fireHistoryModalRef} />
      <PropertyBoundaryControl layer={propertyBoundaryLayer} filters={propertyBoundaryFilters} onFiltersChange={onPropertyBoundaryFiltersChange} expanded={expanded} onExpandedChange={setExpanded} />
      <RealtimeControl
        incidentsLayer={incidentsLayer}
        incidentDays={incidentDays}
        onIncidentDaysChange={onIncidentDaysChange}
        incidentGroups={incidentGroups}
        onIncidentGroupsChange={onIncidentGroupsChange}
        hotspotsLayer={hotspotsLayer}
        hotspotsFilters={hotspotsFilters}
        onHotspotsFiltersChange={onHotspotsFiltersChange}
        hotspotsAvailable={hotspotsAvailable}
        lightningLayer={lightningLayer}
        lightningFilters={lightningFilters}
        onLightningFiltersChange={onLightningFiltersChange}
        lightningAvailable={lightningAvailable}
        hazardsLayer={hazardsLayer}
        hazardsFilters={hazardsFilters}
        onHazardsFiltersChange={onHazardsFiltersChange}
        hazardsAvailable={hazardsAvailable}
        ignitionPointsLayer={ignitionPointsLayer}
        ignitionPointsFilters={ignitionPointsFilters}
        onIgnitionPointsFiltersChange={onIgnitionPointsFiltersChange}
        ignitionPointsAvailable={ignitionPointsAvailable}
        burntAreasLayer={burntAreasLayer}
        burntAreasFilters={burntAreasFilters}
        onBurntAreasFiltersChange={onBurntAreasFiltersChange}
        burntAreasAvailable={burntAreasAvailable}
        expanded={expanded}
        onExpandedChange={setExpanded}
      />
      <CustomLayersControl layer={customLayersLayer} filters={customLayersFilters} onFiltersChange={onCustomLayersFiltersChange} selectedIds={customLayersSelectedIds} onToggleLayer={onCustomLayersToggle} expanded={expanded} onExpandedChange={setExpanded} />
      
      <Box sx={{ height: "16px" }} />
      
      <MeasureControl layer={measureLayer} expanded={expanded} onExpandedChange={setExpanded} />
    </Box>
  );
};

export default LayerControlPanel;
